import React from 'react';
import { 
  Shield, 
  Lock, 
  Database,
  Settings,
  Users,
  Cookie,
  CheckCircle,
  Baby,
  Calendar,
  Mail,
  Globe,
  Trash2,
  AlertCircle
} from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-400/10 rounded-lg">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Last updated: 04/11/2025
          </p>
          <p className="text-lg text-gray-300">
            At <strong className="text-white">Fokat Main</strong>, operated by{' '}
            <strong className="text-white">PLAYEDGE INNOVATIONS</strong>, your privacy is important to us. 
            This Privacy Policy explains how we collect, use, store, and protect your personal information when 
            you visit or participate in any activity on{' '}
            <a href="http://www.fokatmain.com" className="text-yellow-400 hover:underline">
              www.fokatmain.com
            </a>. By using our website, you agree to the terms described below.
          </p>
        </header>

        {/* Content Card */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-800 space-y-8">
          
          {/* Section 1: Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">1.</span> Information We Collect
            </h2>
            <p className="text-gray-300 mb-4">
              We may collect the following information from users:
            </p>
            <div className="space-y-3">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  <strong className="text-white">Basic details:</strong> Name, email address, phone number, and city.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  <strong className="text-white">Participation data:</strong> Quiz responses, contest entries, and feedback submissions.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  <strong className="text-white">Technical data:</strong> Device type, browser, IP address, and cookies for website analytics.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  <strong className="text-white">Payment details:</strong> When applicable, limited transaction details are collected 
                  securely through our payment gateway partners (we do not store card or UPI data on our servers).
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">2.</span> How We Use Your Information
            </h2>
            <p className="text-gray-300 mb-4">
              Your information is used strictly for legitimate purposes such as:
            </p>
            <div className="space-y-3">
              {[
                "Managing quiz entries and giveaway logistics.",
                "Notifying winners and dispatching prizes.",
                "Improving user experience, content, and quiz performance.",
                "Sending platform updates or promotional offers (only if you opt-in).",
                "Maintaining compliance with applicable laws and payment regulations."
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3: Data Protection and Security */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">3.</span> Data Protection and Security
            </h2>
            <p className="text-gray-300 mb-4">
              We take data security seriously.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  All personal and payment data is <strong className="text-white">encrypted</strong> and transmitted 
                  over secure (HTTPS) connections.
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Access to user data is <strong className="text-white">limited to authorized personnel only</strong>.
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  We <strong className="text-white">never sell, rent, or trade</strong> your information with third parties.
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Sensitive information like card or UPI details is <strong className="text-white">never stored</strong> on 
                  our platform — it is handled by trusted, PCI-DSS-compliant payment processors.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Cookies and Analytics */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Cookie className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">4.</span> Cookies and Analytics
            </h2>
            <div className="space-y-3">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  Fokat Main uses cookies to enhance browsing and track quiz performance analytics.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  Cookies help us understand user activity but <strong className="text-white">do not collect personal information</strong>.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  You can disable cookies in your browser settings, but some site functions may be affected.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">5.</span> Third-Party Services
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300 mb-3">
                We may use third-party tools for analytics, payments, or social media integration. 
                These services (such as Cashfree Payments or Google Analytics) have their own privacy policies.
              </p>
              <p className="text-gray-300">
                We recommend reviewing their policies to understand how they handle your data.
              </p>
            </div>
          </section>

          {/* Section 6: User Rights */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">6.</span> User Rights
            </h2>
            <p className="text-gray-300 mb-4">
              You have the right to:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  <strong className="text-white">Access</strong> or request correction of your personal data.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  <strong className="text-white">Request deletion</strong> of your data (subject to legal and transactional obligations).
                </p>
              </div>
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  <strong className="text-white">Withdraw consent</strong> for promotional communications at any time.
                </p>
              </div>
            </div>
            <div className="mt-4 bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
              <p className="text-gray-300">
                To exercise these rights, please email us at{' '}
                <a href="mailto:support@fokatmain.com" className="text-yellow-400 hover:underline font-semibold">
                  support@fokatmain.com
                </a>.
              </p>
            </div>
          </section>

          {/* Section 7: Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">7.</span> Data Retention
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300">
                We retain user information only for as long as necessary to fulfill quiz participation, prize delivery, 
                and legal or regulatory requirements. After this period, data is securely deleted or anonymized.
              </p>
            </div>
          </section>

          {/* Section 8: Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Baby className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">8.</span> Children's Privacy
            </h2>
            <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 mb-2">
                    Our platform is intended for users aged <strong className="text-white">18 years and above</strong>.
                  </p>
                  <p className="text-gray-300">
                    We do not knowingly collect data from minors. If we discover that information from a user under 18 has 
                    been submitted, it will be promptly removed.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 9: Updates to This Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">9.</span> Updates to This Policy
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300">
                We may update this Privacy Policy from time to time to reflect operational or legal changes. 
                The revised version will always be available at{' '}
                <a href="https://www.fokatmain.com/privacy" className="text-yellow-400 hover:underline">
                  https://www.fokatmain.com/privacy
                </a>{' '}
                with a new "Last Updated" date.
              </p>
            </div>
          </section>

          {/* Section 10: Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-blue-400" />
              <span className="text-yellow-400">10.</span> Contact Us
            </h2>
            <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-800/50 rounded-lg p-6">
              <p className="text-gray-300 mb-4">
                If you have questions or concerns regarding privacy or data handling, please contact us at:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <a href="mailto:support@fokatmain.com" className="text-yellow-400 hover:underline font-semibold">
                    support@fokatmain.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-yellow-400" />
                  <a href="/contact" className="text-yellow-400 hover:underline font-semibold">
                    https://www.fokatmain.com/contact
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <div className="pt-6 border-t border-gray-800">
            <p className="text-sm text-gray-400 mb-3">Related Pages:</p>
            <div className="flex flex-wrap gap-3">
              <a 
                href="/faq" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                FAQ
              </a>
              <a 
                href="/terms" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Terms & Conditions
              </a>
              <a 
                href="/refund-policy" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Refund Policy
              </a>
              <a 
                href="/contact" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
